import { useEffect, useId, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { IconButton } from "./primitives.js";

export type DialogVariant = "center" | "top" | "sheet" | "drawer";
export type DialogSize = "sm" | "md" | "lg";

export interface DialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly description?: string | undefined;
  readonly variant?: DialogVariant | undefined;
  readonly size?: DialogSize | undefined;
  readonly dismissible?: boolean | undefined;
  readonly footer?: ReactNode;
  readonly children: ReactNode;
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

const SIZE: Record<DialogSize, string> = {
  sm: "max-w-[26rem]",
  md: "max-w-[32rem]",
  lg: "max-w-[42rem]"
};

const WRAPPER: Record<DialogVariant, string> = {
  center: "items-center justify-center p-4",
  top: "items-start justify-center p-4 pt-[10vh]",
  sheet: "justify-end",
  drawer: "justify-start"
};

const PANEL: Record<DialogVariant, string> = {
  center: "max-h-[min(88dvh,48rem)] w-full rounded-xl border border-line-strong animate-pop",
  top: "max-h-[70dvh] w-full rounded-xl border border-line-strong animate-pop",
  sheet: "h-full w-full max-w-[32rem] border-l border-line-strong animate-slide-in-right",
  drawer: "h-full w-[17rem] max-w-[86vw] border-r border-line-strong animate-slide-in-left"
};

/** Modal dialog with focus trap, Escape handling, scroll lock, and focus restore. */
export function Dialog(props: DialogProps): ReactNode {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const onCloseRef = useRef(props.onClose);
  const dismissible = props.dismissible !== false;
  const variant = props.variant ?? "center";

  useEffect((): void => {
    onCloseRef.current = props.onClose;
  }, [props.onClose]);

  useEffect((): (() => void) | undefined => {
    if (!props.open) {
      return undefined;
    }
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>("[data-autofocus]") ?? panel?.querySelector<HTMLElement>(FOCUSABLE) ?? panel;
    first?.focus();

    function onKey(event: globalThis.KeyboardEvent): void {
      if (event.key === "Escape" && dismissible) {
        onCloseRef.current();
      }
    }
    document.addEventListener("keydown", onKey);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [props.open, dismissible]);

  function onPanelKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key !== "Tab" || panelRef.current === null) {
      return;
    }
    const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    const first = items[0];
    const last = items[items.length - 1];
    if (first === undefined || last === undefined) {
      event.preventDefault();
      return;
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  if (!props.open) {
    return null;
  }

  const panelSize = variant === "center" || variant === "top" ? SIZE[props.size ?? "md"] : "";

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        aria-hidden="true"
        onClick={dismissible ? props.onClose : undefined}
        className="absolute inset-0 bg-[#010a0c]/75 animate-fade-in"
      />
      <div className={`absolute inset-0 flex ${WRAPPER[variant]}`}>
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={props.description !== undefined ? descriptionId : undefined}
          tabIndex={-1}
          onKeyDown={onPanelKeyDown}
          className={`relative flex flex-col bg-ink-2 shadow-pop outline-none ${PANEL[variant]} ${panelSize}`}
        >
          <header className="flex items-start gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0 flex-1">
              <h2 id={titleId} className="text-base font-semibold text-foam">
                {props.title}
              </h2>
              {props.description !== undefined ? (
                <p id={descriptionId} className="mt-0.5 text-sm leading-relaxed text-mist">
                  {props.description}
                </p>
              ) : null}
            </div>
            {dismissible ? <IconButton label="Schließen" icon={X} size="sm" onClick={props.onClose} /> : null}
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto">{props.children}</div>
          {props.footer !== undefined ? <footer className="border-t border-line px-5 py-3.5">{props.footer}</footer> : null}
        </div>
      </div>
    </div>,
    document.body
  );
}
